import { ReactNode, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { DraftDefaults, resolveDefaultDraftDefaults } from "./resolveDefaultDraftDefaults.util";
import { findMeAmongSenders } from "./resolveMyDefaultSender.util";

import type { User } from "@/pages/Public/pages/Login/schema/login";
import type { ListPerson } from "@/pages/Private/pages/List/schema/list";

import type { SenderRecipientOption } from "../components/SenderRecipientDropdown";

interface UseDraftDefaultsParams {
	lead: ListPerson | undefined;
	senderOptions: SenderRecipientOption[];
	recipientOptions: SenderRecipientOption[];
	sendingUsers: User[] | undefined;
	user: User | null | undefined;
	connectionCheckDone: boolean;
	connectedUserIds: Set<number>;
	/** The sender on the draft being edited: the hint below only applies while it is the user. */
	activeFromUserId: number | undefined;
}

interface DraftDefaultsState {
	draftDefaults: DraftDefaults;
	/** "Anna Müller's account isn't connected anymore — sending as you" (PREC-4312, E5), or null. */
	fallbackNotice: ReactNode;
	/** The current user's sending entry, undefined for a user with no sender of their own. */
	me: User | undefined;
}

/**
 * The composer's default sender and recipient for the current lead, resolved once per render from
 * {@link resolveDefaultDraftDefaults} so every reader in `useAddInboxChat` (a new lead's drafts,
 * the placeholder fill after the connection check, Clear all) reads the same answer. Extracted from
 * `useAddInboxChat`, which may not grow.
 */
export const useDraftDefaults = ({
	lead,
	senderOptions,
	recipientOptions,
	sendingUsers,
	user,
	connectionCheckDone,
	connectedUserIds,
	activeFromUserId,
}: UseDraftDefaultsParams): DraftDefaultsState => {
	const { t } = useTranslation();

	const draftDefaults = useMemo(
		() =>
			resolveDefaultDraftDefaults({
				conversations: lead?.prospect?.conversations ?? [],
				listPersonId: lead?.id,
				prospect: lead?.prospect,
				senderOptions,
				recipientOptions,
				sendingUsers: sendingUsers ?? [],
				currentUser: user,
				connectionCheckDone,
				connectedUserIds,
			}),
		[
			lead,
			senderOptions,
			recipientOptions,
			sendingUsers,
			user,
			connectionCheckDone,
			connectedUserIds,
		]
	);

	const gone = draftDefaults.goneSender;
	const showNotice = !!gone && !!user?.id && activeFromUserId === user.id;
	const named = !!gone?.name && gone.userId !== user?.id;

	const fallbackNotice = showNotice ? (
		<div className="px-4 pb-1 text-xs text-status-warning" data-testid="sender-fallback-notice">
			{named
				? t("inbox.identity.goneSender", { name: gone.name })
				: t("inbox.identity.goneSenderUnnamed")}
		</div>
	) : null;

	const me = useMemo(() => findMeAmongSenders(sendingUsers, user?.id), [sendingUsers, user?.id]);

	return { draftDefaults, fallbackNotice, me };
};
